import { toast } from '../../../toast/toast';
import { toastMutationError } from '../../../query/mutationToasts';
import type { KnowledgeBaseDocument } from '../../../types/knowledge';
import type { WithResourceId } from '../../../types/knowledge';
import {
  allSelectedSupport,
  canResumeAbandonedDocument,
} from '../documentActionEligibility';
import type { I18nContextValue } from '../../../types/i18nContext';

type Translate = I18nContextValue['translate'];

type MutateLike<TVariables> = {
  mutate: (
    variables: TVariables,
    options?: {
      onSuccess?: () => void;
      onError?: (error: unknown) => void;
    },
  ) => void;
};

export function mutateOnePromise<TVariables>(
  mutation: MutateLike<TVariables>,
  variables: TVariables,
  errorTitle: string,
  errorFallbackDescription = '',
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    mutation.mutate(variables, {
      onSuccess: () => resolve(true),
      onError: (error) => {
        void toastMutationError(error, errorTitle, errorFallbackDescription);
        resolve(false);
      },
    });
  });
}
export async function runSequentialMutations<TItem>(options: {
  items: TItem[];
  mutateOne: (item: TItem) => Promise<boolean>;
  onProgress?: (progress: { completed: number; total: number }) => void;
}): Promise<number> {
  const total = options.items.length;
  let successCount = 0;

  options.onProgress?.({ completed: 0, total });

  for (let index = 0; index < options.items.length; index += 1) {
    const item = options.items[index];
    if (item !== undefined && (await options.mutateOne(item))) {
      successCount += 1;
    }
    options.onProgress?.({ completed: index + 1, total });
  }

  return successCount;
}

export async function runBulkDownloadDocuments(options: {
  targets: Array<WithResourceId<KnowledgeBaseDocument>>;
  mutateOne: (
    document: WithResourceId<KnowledgeBaseDocument>,
  ) => Promise<boolean>;
  translate: Translate;
}): Promise<number> {
  const successCount = await runSequentialMutations({
    items: options.targets,
    mutateOne: options.mutateOne,
  });

  if (successCount > 1) {
    toast.success({
      title: options.translate('knowledge.downloadBatchSuccessTitle'),
      description: options.translate(
        'knowledge.downloadBatchSuccessDescription',
        { count: String(successCount) },
      ),
    });
  }

  return successCount;
}

export async function runBulkResumeAbandonedDocuments(options: {
  targets: Array<WithResourceId<KnowledgeBaseDocument>>;
  mutateOne: (
    document: WithResourceId<KnowledgeBaseDocument>,
  ) => Promise<boolean>;
  translate: Translate;
}): Promise<number> {
  if (!allSelectedSupport(options.targets, canResumeAbandonedDocument)) {
    return 0;
  }

  const successCount = await runSequentialMutations({
    items: options.targets,
    mutateOne: options.mutateOne,
  });

  if (successCount === 1) {
    toast.success({
      title: options.translate('knowledge.documentResumeSuccessTitle'),
      description: options.translate(
        'knowledge.documentResumeSuccessDescription',
      ),
    });
  } else if (successCount > 1) {
    toast.success({
      title: options.translate('knowledge.documentResumeBatchSuccessTitle'),
      description: options.translate(
        'knowledge.documentResumeBatchSuccessDescription',
        { count: String(successCount) },
      ),
    });
  }

  return successCount;
}

export async function runBulkDeleteDocuments(options: {
  targets: Array<WithResourceId<KnowledgeBaseDocument>>;
  mutateOne: (
    document: WithResourceId<KnowledgeBaseDocument>,
  ) => Promise<boolean>;
  translate: Translate;
  onProgress?: (progress: { completed: number; total: number }) => void;
}): Promise<number> {
  const successCount = await runSequentialMutations({
    items: options.targets,
    mutateOne: options.mutateOne,
    onProgress: options.onProgress,
  });

  if (successCount > 0) {
    toast.success({
      title: options.translate('knowledge.deleteBatchSuccessTitle'),
      description: options.translate(
        'knowledge.deleteBatchSuccessDescription',
        {
          count: String(successCount),
        },
      ),
    });
  }

  return successCount;
}
